// Audio clips (spec §19). Shared so the client and server agree on formats and limits.

export const AUDIO = {
  /** Longest recording or file, in seconds. */
  maxSeconds: 600,
  /** What validation allows over maxSeconds: a recording stopped at ten minutes can report a little more. */
  slackSeconds: 10,
  /** Levels drawn as the waveform. */
  peaks: 64,
  /** The player's starting size; resizing keeps this shape. */
  width: 320,
  height: 80,
  /** Recording bitrate (mono): about 0.5 MB a minute. */
  bitrate: 64_000,
  titleLength: 200,
} as const;

/** Formats a clip can be in: what browsers record, and common audio files. */
export const AUDIO_TYPES = ['audio/mp4', 'audio/webm', 'audio/ogg', 'audio/mpeg', 'audio/wav', 'audio/flac'] as const;
export type AudioType = (typeof AUDIO_TYPES)[number];

/** File extensions, for downloads and the account export. */
export const AUDIO_EXT: Record<AudioType, string> = {
  'audio/mp4': 'm4a', 'audio/webm': 'webm', 'audio/ogg': 'ogg', 'audio/mpeg': 'mp3', 'audio/wav': 'wav', 'audio/flac': 'flac',
};

/** The format of some audio, read from its first bytes (never from a name or a header), or null. */
export function sniffAudio(b: Uint8Array): AudioType | null {
  const ascii = (from: number, to: number) => (b.length >= to ? String.fromCharCode(...b.subarray(from, to)) : '');
  if (ascii(4, 8) === 'ftyp') return 'audio/mp4';
  if (b.length >= 4 && b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) return 'audio/webm';
  if (ascii(0, 4) === 'OggS') return 'audio/ogg';
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WAVE') return 'audio/wav';
  if (ascii(0, 4) === 'fLaC') return 'audio/flac';
  if (ascii(0, 3) === 'ID3') return 'audio/mpeg';
  // An MPEG audio frame header: 11 sync bits, then a layer other than "reserved" (which ADTS AAC uses).
  if (b.length >= 2 && b[0] === 0xff && (b[1]! & 0xe0) === 0xe0 && ((b[1]! >> 1) & 3) !== 0) return 'audio/mpeg';
  return null;
}

/** 83 → "1:23". */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
